import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  searchReferringDoctors,
  type ReferringDoctorResult,
} from "~/api/referringDoctors";
import { tw } from "~/utils";

interface ReferralTypeaheadProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: ReferringDoctorResult | null) => void;
  placeholder?: string;
  id?: string;
}

const NO_RESULTS: ReferringDoctorResult[] = [];

const resultKey = (result: ReferringDoctorResult) =>
  `${result.practice_id}-${result.doctor_id ?? "practice"}`;

export const ReferralTypeahead = ({
  value,
  onChange,
  onSelect,
  placeholder,
  id = "referring-doctor",
}: ReferralTypeaheadProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Once a result is picked the text is its label; refocusing shouldn't offer
  // the list again until the patient edits the text.
  const [hasSelection, setHasSelection] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listboxId = `${useId()}-listbox`;

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [value]);

  const { data: results = NO_RESULTS } = useQuery({
    queryKey: ["referring-doctors", "search", debouncedQuery],
    queryFn: () => searchReferringDoctors(debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30_000,
  });

  // A new result set invalidates the highlighted position.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isExpanded = isOpen && results.length > 0;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      onSelect(null);
      setHasSelection(false);
      setIsOpen(true);
    },
    [onChange, onSelect],
  );

  const handleSelect = useCallback(
    (result: ReferringDoctorResult) => {
      onChange(result.label);
      onSelect(result);
      setHasSelection(true);
      setIsOpen(false);
    },
    [onChange, onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex((index) => Math.min(index + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const active = results[activeIndex];
        // Only claim Enter when it picks something; otherwise let it submit.
        if (isExpanded && active) {
          e.preventDefault();
          handleSelect(active);
        }
        break;
      }
      case "Escape":
        if (isExpanded) {
          e.preventDefault();
          setIsOpen(false);
        }
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        id={id}
        role="combobox"
        aria-expanded={isExpanded}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (!hasSelection) setIsOpen(true);
        }}
        placeholder={placeholder}
        className="block w-full rounded-md border border-gray-300 px-4 py-3 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
        autoComplete="off"
      />
      {isExpanded && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg"
        >
          {results.map((result, index) => (
            <li
              key={resultKey(result)}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              // Keep focus on the input so the click lands before any blur.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setActiveIndex(index)}
              className={tw(
                "w-full cursor-pointer px-4 py-3 text-left text-sm",
                index === activeIndex && "bg-gray-100",
              )}
            >
              {result.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
